import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';

/**
 * GPS轨迹线组
 */
let group_gps_trajectory = null;

/**
  * 存储所有GPS点的数组 - 完整缓存的数据
 */
let gpsPointsCache = [];

/**
 * 上一帧的时间戳，用于检测时间跳转
 */
let lastTimestamp = null;

/**
 * 最大保存点数（可根据需要调整）
 */
const MAX_GPS_POINTS = 10000;

/**
 * GPS轨迹线是否启用
 */
let isGpsTrajectoryEnabled = true;

/**
 * GPS轨迹线材质 - 使用 LineMaterial 支持真正的线宽
 */
const material_gps_trajectory = new LineMaterial({
  color: 0x00ff00, // 绿色轨迹线
  linewidth: 0.004, // 线宽，单位是相对于屏幕尺寸的比例
  transparent: true,
  opacity: 0.8,
  depthTest: false,
  worldUnits: false, // 使用屏幕空间单位
});

/**
 * 当前位置三角形标识材质
 */
const material_current_marker = new THREE.MeshBasicMaterial({
  color: 0xff0000, // 红色三角形
  transparent: true,
  opacity: 0.9,
  depthTest: false,
  side: THREE.DoubleSide,
});

/**
 * 当前位置标识mesh
 */
let current_position_marker = null;

/**
 * 绘制GPS轨迹线
 * @param {Object} vehicleData - 车辆数据
 * @param {Object} scene - Three.js场景对象
 * @param {Number} currentTimestamp - 当前帧时间戳（毫秒）
 */
export const load_gps_pose_trajectory = (vehicleData, scene, currentTimestamp) => {
  // 如果GPS轨迹线功能被关闭，隐藏并返回
  if (!isGpsTrajectoryEnabled) {
    if (group_gps_trajectory) {
      group_gps_trajectory.visible = false;
    }
    return;
  }

  if (!vehicleData?.localization?.gpsPose) {
    if (group_gps_trajectory) {
      group_gps_trajectory.visible = false;
    }
    return;
  }

  const gpsPose = vehicleData.localization.gpsPose;
  
  // 检测时间跳跃（包括回退和切包）
  if (lastTimestamp !== null && currentTimestamp !== undefined) {
    const timeDiff = Math.abs(currentTimestamp - lastTimestamp);
    const TIME_JUMP_THRESHOLD = 60000; // 60秒，超过此值认为是切包或大幅跳转
    
    if (currentTimestamp < lastTimestamp) {
      // 情况1：时间回退（往回拖拽进度条）- 删除未来的点
      console.log(`检测到时间回退: ${lastTimestamp}ms → ${currentTimestamp}ms，删除未来的轨迹点`);
      gpsPointsCache = gpsPointsCache.filter(point => point.timestamp <= currentTimestamp);
    } else if (timeDiff > TIME_JUMP_THRESHOLD) {
      // 情况2：时间大幅前跳（切包或大幅拖动） - 清空所有缓存，重新开始
      console.log(`检测到时间段切换: 时间跳跃${(timeDiff/1000).toFixed(1)}秒，清空GPS轨迹缓存`);
      gpsPointsCache = [];
    }
  }
  
  // 更新最后时间戳
  lastTimestamp = currentTimestamp;
  
  // 收集GPS点数据到缓存（去重：避免重复添加相同时间戳的点）
  if (gpsPose.x !== undefined && gpsPose.y !== undefined) {
    const existingIndex = gpsPointsCache.findIndex(point => point.timestamp === currentTimestamp);
    if (existingIndex === -1) {
      gpsPointsCache.push({
        x: gpsPose.x,
        y: gpsPose.y,
        z: 0.08,
        timestamp: currentTimestamp,
      });

      // 如果超过最大点数，删除最旧的点
      if (gpsPointsCache.length > MAX_GPS_POINTS) {
        gpsPointsCache.shift();
      }
    }
  }

  // 从缓存中获取需要显示的点（当前时间之前的所有点）
  const displayPoints = gpsPointsCache.filter(point => point.timestamp <= currentTimestamp);

  // 如果点数少于2个，无法绘制线
  if (displayPoints.length < 2) {
    return;
  }

  // 创建或更新GPS轨迹线组
  if (!group_gps_trajectory) {
    group_gps_trajectory = new THREE.Group();
    group_gps_trajectory.name = 'gps_trajectory_group';
    scene.add(group_gps_trajectory);
  } else {
    // 清空之前的线条
    while (group_gps_trajectory.children.length > 0) {
      const child = group_gps_trajectory.children[0];
      if (child.geometry) child.geometry.dispose();
      if (child.material) child.material.dispose();
      group_gps_trajectory.remove(child);
    }
  }

  // 创建点数组
  const positions = [];
  displayPoints.forEach((point) => {
    positions.push(point.x, point.y, point.z);
  });

  // 创建轨迹线 - 使用 Line2 支持真正的线宽
  const geometry = new LineGeometry();
  geometry.setPositions(positions);
  
  const line = new Line2(geometry, material_gps_trajectory);
  line.computeLineDistances();
  line.renderOrder = 50;
  line.name = 'gps_trajectory_line';
  
  group_gps_trajectory.add(line);

  // 创建当前位置的三角形标识
  createCurrentPositionMarker(gpsPose);
  
  group_gps_trajectory.visible = true;
};

/**
 * 创建当前位置的三角形标识
 * @param {Object} gpsPose - 当前GPS位置 {x, y, z}
 */
const createCurrentPositionMarker = (gpsPose) => {
  if (!group_gps_trajectory) return;

  // 创建三角形几何体
  const triangleSize = 0.5; // 三角形大小
  const triangleHeight = triangleSize * Math.sqrt(3) / 2;
  
  const triangleShape = new THREE.Shape();
  triangleShape.moveTo(0, triangleHeight * 2 / 3); // 顶点
  triangleShape.lineTo(-triangleSize / 2, -triangleHeight / 3); // 左下
  triangleShape.lineTo(triangleSize / 2, -triangleHeight / 3); // 右下
  triangleShape.lineTo(0, triangleHeight * 2 / 3); // 回到顶点

  const geometry = new THREE.ShapeGeometry(triangleShape);
  current_position_marker = new THREE.Mesh(geometry, material_current_marker);
  
  // 设置位置
  const z = 0.08;
  current_position_marker.position.set(gpsPose.x, gpsPose.y, z);
  
  // 设置旋转，使三角形指向上方
  current_position_marker.rotation.x = 0;
  current_position_marker.rotation.y = 0;
  current_position_marker.rotation.z = 0;
  
  current_position_marker.renderOrder = 51; // 确保在轨迹线之上
  current_position_marker.name = 'gps_current_marker';
  
  group_gps_trajectory.add(current_position_marker);
};

/**
 * 清除GPS轨迹线
 */
export const clear_gps_pose_trajectory = () => {
  if (group_gps_trajectory) {
    while (group_gps_trajectory.children.length > 0) {
      const child = group_gps_trajectory.children[0];
      if (child.geometry) child.geometry.dispose();
      if (child.material) child.material.dispose();
      group_gps_trajectory.remove(child);
    }
    if (group_gps_trajectory.parent) {
      group_gps_trajectory.parent.remove(group_gps_trajectory);
    }
    group_gps_trajectory = null;
  }
  current_position_marker = null;
  gpsPointsCache = [];
  lastTimestamp = null;
};

/**
 * 设置GPS轨迹线可见性
 * @param {boolean} visible - 是否可见
 */
export const set_gps_trajectory_visible = (visible) => {
  isGpsTrajectoryEnabled = visible;
  if (group_gps_trajectory) {
    group_gps_trajectory.visible = visible;
  }
};

/**
 * 获取GPS轨迹线启用状态
 * @returns {boolean} 是否启用
 */
export const get_gps_trajectory_enabled = () => {
  return isGpsTrajectoryEnabled;
};

/**
 * 重置GPS轨迹历史记录（保留group，仅清空历史点）
 */
export const reset_gps_trajectory_history = () => {
  gpsPointsCache = [];
  lastTimestamp = null;
  current_position_marker = null;
  if (group_gps_trajectory) {
    while (group_gps_trajectory.children.length > 0) {
      const child = group_gps_trajectory.children[0];
      if (child.geometry) child.geometry.dispose();
      group_gps_trajectory.remove(child);
    }
  }
};

